import { useCallback, useEffect, useRef, useState } from 'react';
import type { OAuthFlowState, OAuthInfo, OAuthPurpose, OAuthStart } from '@momomail/shared';
import { api, bridge, errorText } from '../lib/bridge';
import { t, tMessage } from '../lib/i18n';
import { Icon } from './Icon';
import { Dialog } from './Overlay';
import { toast } from './Toasts';

/**
 * "用 Google 登录" (sign in with Google): adding Gmail, re-authorizing, and connecting Google Calendar all go through here.
 * The backend provides the Google authorization URL → the user consents in the browser → Google redirects back to the backend, which finishes on its own; here we poll every 1.5 seconds to see if it's done.
 * Without a public URL configured (loopback), the callback address is 127.0.0.1, which won't open if the browser isn't on the backend's machine: have the user paste back the URL from the address bar.
 */

let infoPromise: Promise<OAuthInfo> | null = null;

/** Whether the server has Google sign-in configured (asked only once). */
export function googleInfo(): Promise<OAuthInfo> {
  infoPromise ??= api()
    .google.info()
    .catch(() => {
      infoPromise = null;
      return { google: false, redirectUri: null };
    });
  return infoPromise;
}

export function useGoogleInfo(): OAuthInfo | null {
  const [info, setInfo] = useState<OAuthInfo | null>(null);
  useEffect(() => {
    let alive = true;
    void googleInfo().then((i) => alive && setInfo(i));
    return () => {
      alive = false;
    };
  }, []);
  return info;
}

export interface GoogleFlowRequest {
  purpose: OAuthPurpose;
  accountId?: string;
  loginHint?: string;
}

type Pending = GoogleFlowRequest & { resolve: (s: OAuthFlowState | null) => void };
let openFlow: ((p: Pending) => void) | null = null;

/** Open the Google sign-in dialog; returns the flow state on completion, null on cancel. */
export function startGoogleFlow(request: GoogleFlowRequest): Promise<OAuthFlowState | null> {
  return new Promise((resolve) => {
    if (!openFlow) return resolve(null);
    openFlow({ ...request, resolve });
  });
}

const POLL_MS = 1500;
const MAX_MS = 30 * 60_000;

const TITLES = (): Record<OAuthPurpose, string> => ({ mail: t('用 Google 登录 Gmail'), calendar: t('连接 Google 日历') });

export function GoogleFlowHost() {
  const [pending, setPending] = useState<Pending | null>(null);
  const [shown, setShown] = useState(false);
  const [start, setStart] = useState<OAuthStart | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paste, setPaste] = useState('');
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const settled = useRef(false);
  const pendingRef = useRef<Pending | null>(null);
  pendingRef.current = pending;

  useEffect(() => {
    openFlow = (p) => {
      settled.current = false;
      setPending(p);
      setStart(null);
      setError(null);
      setPaste('');
      setAttempt((n) => n + 1);
      setShown(true);
    };
    return () => {
      openFlow = null;
    };
  }, []);

  // Stable reference: a changed onClose makes the dialog grab focus again (can't have that while typing in the input)
  const finishWith = useCallback((state: OAuthFlowState | null) => {
    if (settled.current) return;
    settled.current = true;
    pendingRef.current?.resolve(state);
    setShown(false);
    if (state?.status === 'done') toast(state.purpose === 'calendar' ? t('已连接 {email} 的 Google 日历', { email: state.email ?? '' }) : t('{email} 已登录，正在收信…', { email: state.email ?? 'Gmail' }), 'ok');
  }, []);
  const cancel = useCallback(() => finishWith(null), [finishWith]);

  // Start: get the authorization URL (the desktop app opens the browser directly; on the web the user must click, or the popup gets blocked)
  useEffect(() => {
    if (!pending || !shown) return;
    let alive = true;
    const { purpose, accountId, loginHint } = pending;
    api()
      .google.start(purpose, { ...(accountId ? { accountId } : {}), ...(loginHint ? { loginHint } : {}) })
      .then((s) => {
        if (!alive) return;
        setStart(s);
        if (bridge.desktop) void api().mail.openExternal(s.url);
      })
      .catch((e) => alive && setError(errorText(e)));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  // Poll: done once the backend receives Google's callback
  useEffect(() => {
    if (!start || !shown || error) return;
    const began = Date.now();
    let alive = true;
    const timer = setInterval(() => {
      if (Date.now() - began > MAX_MS) {
        setError(t('等了 30 分钟还没完成，请重新开始。'));
        return;
      }
      api()
        .google.flow(start.flowId)
        .then((s) => {
          if (!alive) return;
          if (s.status === 'done') finishWith(s);
          else if (s.status === 'error') setError(s.error ? tMessage(s.error) : t('授权失败'));
        })
        .catch((e) => alive && setError(errorText(e)));
    }, POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, shown, error]);

  const submitPaste = async () => {
    if (!start || !paste.trim()) return;
    setBusy(true);
    try {
      const s = await api().google.finish(start.flowId, paste.trim());
      if (s.status === 'done') finishWith(s);
      else setError(s.error ? tMessage(s.error) : t('授权失败'));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!start) return;
    try {
      await navigator.clipboard.writeText(start.url);
      toast(t('链接已复制'), 'ok');
    } catch {
      toast(t('复制失败，请手动选中链接复制'), 'error');
    }
  };

  const purpose = pending?.purpose ?? 'mail';
  return (
    <Dialog
      open={shown}
      onClose={cancel}
      title={TITLES()[purpose]}
      labelledBy="google-flow-title"
      className="dialog__card--small google-flow"
      footer={
        <>
          <button className="btn btn--quiet" onClick={() => finishWith(null)}>
            {t('取消')}
          </button>
          {error && (
            <button className="btn btn--primary" onClick={() => (setError(null), setStart(null), setPaste(''), setAttempt((n) => n + 1))}>
              <Icon name="refresh" size={15} /> {t('重新开始')}
            </button>
          )}
        </>
      }
    >
      {error ? (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      ) : !start ? (
        <p className="muted google-flow__wait">
          <span className="spinner" /> {t('正在准备 Google 登录…')}
        </p>
      ) : (
        <div className="google-flow__steps">
          <p>
            {bridge.desktop ? t('已在浏览器里打开 Google 登录页。') : t('在新标签页里打开 Google 登录页，')}
            {t('登录{who}并点"允许"，完成后这里会自动继续。', { who: pending?.loginHint ? ` ${pending.loginHint}` : t('你的 Google 账号') })}
          </p>
          <div className="google-flow__actions">
            <button className="btn btn--primary" data-autofocus onClick={() => void api().mail.openExternal(start.url)}>
              <Icon name="external" size={15} /> {bridge.desktop ? t('再次打开登录页') : t('打开 Google 登录页')}
            </button>
            <button className="btn btn--quiet btn--small" onClick={() => void copy()} title={t('复制链接，在别的设备上打开')}>
              <Icon name="copy" size={14} /> {t('复制链接')}
            </button>
          </div>
          <p className="muted google-flow__wait">
            <span className="spinner" /> {t('等待 Google 授权…')}
          </p>
          {purpose === 'mail' && <p className="muted google-flow__hint">{t('Google 会提示"此应用未经验证"时，点"高级"→"继续"。这是你自己的服务器。')}</p>}
          {start.mode === 'loopback' && (
            <div className="google-flow__paste">
              <label className="field">
                <span className="field__label">{t('浏览器显示"无法访问"时，把地址栏里的网址粘贴到这里')}</span>
                <input value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="http://127.0.0.1:53682/?state=…&code=…" onKeyDown={(e) => e.key === 'Enter' && void submitPaste()} />
              </label>
              <button className="btn btn--small" disabled={!paste.trim() || busy} onClick={() => void submitPaste()}>
                {busy ? <span className="spinner" /> : <Icon name="check" size={14} />} {t('完成')}
              </button>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
